import { useAtomValue } from 'jotai';
import { RampBar } from 'components/colormaps';
import { formatRangeValue } from '../filter-modal/value-histogram';
import { allDatasetAtoms } from '../atoms';
import {
  selectedSubsurfaceMapAtom,
  subsurfaceColorRuleAtom,
  subsurfaceRasterOnMapIdAtom,
  subsurfaceShownGridRangeAtom,
} from './atoms';
import { ruleStops } from './color-rule';
import { defaultRampStops } from './utils';
import styles from './legend.module.scss';

const datasetAtoms = allDatasetAtoms.subsurfaceMaps;

/**
 * The thin legend under the Subsurface Map swatch: the ramp the selected map is
 * painted with (bands when binned) and the values its ends stand for — the map's
 * own min/max, or the rule's global range. Shows only while the cells are on the map.
 * @return {JSX.Element | null} The legend, or null while there is nothing to read.
 */
export const SubsurfaceMapLegend = () => {
  const shown = useAtomValue(selectedSubsurfaceMapAtom);
  // Set only while the cells are actually on the map: not while they load, nor
  // after Disable.
  const rasterOnMapId = useAtomValue(subsurfaceRasterOnMapIdAtom);
  const gridRange = useAtomValue(subsurfaceShownGridRangeAtom);
  const rule = useAtomValue(subsurfaceColorRuleAtom);
  const visible = useAtomValue(datasetAtoms.onMapAtom);

  if (!shown || rasterOnMapId !== shown.id || !visible) return null;

  const stops = rule
    ? ruleStops(rule)
    : defaultRampStops(shown.defaultColorRamp);
  // A map without built stats is painted across its grid's own range (read off its
  // cells), so that's what the legend labels; no range at all leaves the ends blank.
  const ownRange: [number, number] | null =
    shown.valueMin !== null && shown.valueMax !== null
      ? [shown.valueMin, shown.valueMax]
      : gridRange;
  const range: [number, number] | null =
    rule && !rule.perSurfaceRange ? [rule.rangeMin, rule.rangeMax] : ownRange;
  const units = shown.units ? ` ${shown.units}` : '';

  return (
    <div
      className={styles.legend}
      role="img"
      aria-label={`${shown.mapType} legend`}
    >
      <RampBar
        stops={stops}
        mapping={rule?.mapping}
        binCount={rule?.binCount}
        className={styles.ramp}
      />
      {range && (
        <div className={styles.range}>
          <span>{`${formatRangeValue(range[0])}${units}`}</span>
          <span>{`${formatRangeValue(range[1])}${units}`}</span>
        </div>
      )}
    </div>
  );
};
